import { FC } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  KeyIcon,
  LockClosedIcon,
  EyeSlashIcon,
  DocumentMagnifyingGlassIcon
} from '@heroicons/react/24/outline';

import FYEOLogo from '../../../assets/fyeo.png';
import { FYEO_AUDIT_URL } from '../../../constants/links';

const AUREUS = {
  green: '#6FC2AE',
  blue: '#609EBD',
  navy: '#01263D',
  purple: '#4643D3'
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

const bullets = [
  {
    Icon: KeyIcon,
    title: 'Passkeys first',
    desc: 'Face ID, Touch ID, or a security key — no seed phrases to write down or lose.'
  },
  {
    Icon: LockClosedIcon,
    title: 'True self-custody',
    desc: 'You control key material via Web3Auth + TSS. Oxen Flow cannot move funds for you.'
  },
  {
    Icon: EyeSlashIcon,
    title: 'Data minimization',
    desc: 'We collect only what’s needed for passkey support — not a surveillance-heavy wallet.'
  },
  {
    Icon: DocumentMagnifyingGlassIcon,
    title: 'Independently audited',
    desc: 'Security review by FYEO. Public report available for transparent review.'
  }
] as const;

/**
 * Static security messaging — no interactive wallet surface.
 */
const SecuritySection: FC = () => {
  return (
    <section
      id="security"
      className="py-24 relative overflow-hidden"
      style={{ background: 'rgba(1, 38, 61, 0.4)' }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: AUREUS.green }}
        />
      </div>

      <div className="relative mx-auto max-w-6xl px-6">
        <motion.div
          className="text-center mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <p
            className="text-xs uppercase tracking-[0.25em] mb-4 font-medium"
            style={{ color: AUREUS.blue }}
          >
            Security
          </p>
          <h2 className="text-4xl md:text-5xl font-black mb-5">
            Secure, <span style={{ color: AUREUS.green }}>by design</span>
          </h2>
          <p className="text-xl opacity-80 max-w-2xl mx-auto">
            Non-custodial architecture, passkey auth, and third-party review —
            built for people who want ownership without operational theater.
          </p>
        </motion.div>

        <motion.div
          className="grid sm:grid-cols-2 gap-6 mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {bullets.map(({ Icon, title, desc }) => (
            <motion.div
              key={title}
              className="rounded-2xl border p-7 text-left"
              variants={fadeInUp}
              style={{
                background: 'rgba(1, 38, 61, 0.45)',
                borderColor: 'rgba(96, 158, 189, 0.18)'
              }}
            >
              <div
                className="inline-flex p-3 rounded-xl mb-4"
                style={{ background: 'rgba(111, 194, 174, 0.15)' }}
              >
                <Icon className="h-6 w-6" style={{ color: AUREUS.green }} />
              </div>
              <h3 className="text-xl font-bold mb-2">{title}</h3>
              <p className="opacity-80 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <a
            href={FYEO_AUDIT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl border transition-opacity hover:opacity-90"
            style={{
              background: 'rgba(1, 38, 61, 0.55)',
              borderColor: 'rgba(96, 158, 189, 0.25)'
            }}
          >
            <img src={FYEOLogo} alt="FYEO" className="h-8 w-auto" />
            <span className="text-left">
              <span className="flex items-center gap-1.5 text-sm font-semibold">
                <ShieldCheckIcon
                  className="h-4 w-4 shrink-0"
                  style={{ color: AUREUS.green }}
                />
                FYEO audit report
              </span>
              <span className="block text-xs opacity-70 mt-0.5">
                Read the public security review →
              </span>
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SecuritySection;
